import { User, UserMeta } from '../models/client/user'
import { ApiError } from '../util/errors/ApiError'
import { translateUser } from './tools'
import { apiRequest } from './apiRequest'
import { ApiUser } from '../models/api'
import { BaseError } from '../util/errors/BaseError'
import { EndpointPath } from './constants'
import { GateFeedColumnKey, isObservationKey } from '../models/client/observation'
import { ReportType } from '../models/client/constants'

export const API_USER_PATHS: Record<string, EndpointPath> = {
    users: '/users',
    auth: '/auth',
}

const getUser = async (params: { token: string; userId: string }): Promise<User> => {
    const { token, userId } = params
    if (!token) throw new ApiError({ type: 'Unauthorized' })
    // FIXME: make request to get propper user ... this isn't
    const {
        data: [user],
    } = await apiRequest<ApiUser[]>({
        path: '/users',
        token,
        method: 'GET',
        parameters: {
            userId,
            filter: {
                id: '01778a34-7d55-39d1-0a6c-a26f32af2c80',
            },
        },
    })
    if (!user) {
        throw new ApiError({
            type: 'BadRequest',
            code: 400,
            cause: new Error(`No user by id: ${userId}`),
        })
    }
    return translateUser({ user, token })
}

const loadSelf = async ({ token, userId }: { token: string; userId: string }): Promise<User> => {
    try {
        const {
            data: [user],
        } = await apiRequest<ApiUser[]>({
            path: '/users',
            token,
            method: 'GET',
            parameters: {
                userId,
                filter: {
                    id: userId,
                },
            },
        })
        if (!user) {
            throw new ApiError({
                type: 'BadRequest',
                code: 400,
                cause: new BaseError({ message: `No user by id: '${userId}'` }),
            })
        }

        const nextUser = translateUser({ user, token })
        return nextUser
    } catch (error) {
        console.error('UserClient.loadSelf', error)

        // HACK: force DOM location redirect (ErrorContext is not receiving the error for unknown reason)
        // window.location.href = '/auth'

        throw error
    }
}

const login = async ({
    email,
    password,
}: {
    email: string
    password: string
}): Promise<{
    userId: string
    token: string
    companyId: string
}> => {
    const { data } = await apiRequest<{ token: string; id: string; companyId: string }>({
        path: API_USER_PATHS.auth,
        method: 'POST',
        data: {
            username: email,
            password,
        },
    })

    if (!data) {
        throw new Error('Invalid email or password')
    }

    return {
        userId: data.id,
        token: data.token,
        companyId: data.companyId,
    }
}

const parseObservationKeys = (values: string[]): Array<GateFeedColumnKey> => {
    const columnKeys: Array<GateFeedColumnKey> = []

    values.forEach((key) => {
        if (!isObservationKey(key)) return
        columnKeys.push(key)
    })
    return columnKeys
}

const getUserObservationPreferences = async (params: {
    token?: string | null
    userId?: string | null
    reportType: ReportType
}): Promise<{ columnKeys: Array<GateFeedColumnKey> }> => {
    const { token, userId, reportType } = params
    if (!token || !userId) throw new ApiError({ type: 'BadRequest' })
    const result = await apiRequest<ApiUser>({
        token,
        path: `/users/${userId}`,
        method: 'GET',
    })

    const reportKeys = result?.data?.meta?.reports?.[reportType] || []

    const columnKeys = parseObservationKeys(reportKeys)

    return { columnKeys }
}

const setUserObservationPreferences = async (params: {
    token: string
    userId: string
    columnKeys: Array<GateFeedColumnKey>
    reportType: ReportType
    meta: UserMeta
}): Promise<Array<GateFeedColumnKey>> => {
    const { token, userId, columnKeys, reportType, meta } = params

    const result = await apiRequest<ApiUser>({
        token,
        path: `/users/${userId}`,
        method: 'PATCH',
        data: {
            meta: {
                reports: meta.reports,
            },
        },
    })

    const reportKeys = result?.data?.meta?.reports?.[reportType] || []
    return parseObservationKeys(reportKeys)
}

export const logout = (): Promise<void> => {
    return Promise.resolve()
}

export const userClient = {
    getUser,
    loadSelf,
    login,
    getGateFeedPreferences: getUserObservationPreferences,
    setPreferencesGatefeedColumns: setUserObservationPreferences,
    logout,
}
